import { mapState, mapActions, mapGetters } from 'vuex';
import stickyConstants from '@/helpers/stickyConstants';
import marketFiltersMobile from '@/components/market/market-filters/market-filters-mobile/market-filters-mobile.vue';
import marketSectionHeader from '@/components/market/market-section-header/market-section-header.vue';
import marketListDesktopHeader from '@/components/market/market-list-desktop-header/market-list-desktop-header.vue';
import marketListItemV2 from '@/components/market/v2/market-list-item-v2/market-list-item-v2.vue';
import infiniteLoading from 'vue-infinite-loading';
import headerTagsMixin from '@/mixins/headerTags';
import userAgentMixin from '@/mixins/userAgent';
import loaderMixin from '@/mixins/loader';
import constants from '@/constants';
import log from '@/logger';
import { BRAND } from '@/components/brand-name/brand-name.js';

const HOMEPAGE_FONT_LOAD_FALLBACK_MS = 1200;

export default {
  mixins: [
    headerTagsMixin,
    userAgentMixin,
    loaderMixin
  ],
  components: {
    infiniteLoading,
    marketFiltersMobile,
    marketSectionHeader,
    marketListDesktopHeader,
    marketListItem: marketListItemV2,
  },
  props: {
    notificationText: {
      type: String,
      required: false,
    },
    notificationLevel: {
      type: String,
      required: false,
    },
    headerId: Number,
    headerUrl: String,
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isFiltering: false,
      infiniteLoader: null,
      stickyOptions: stickyConstants.mobile,
      watchersDisabled: false,
      initialLoadDone: false,
      isInfiniteLoading: false,
      embeddedInitialFontsReady: false,
    };
  },
  metaInfo() {
    if (this.embedded) return {};
    if (this.currentHeader !== null) {
      return {
        title: this.currentHeader.seoTitle || 'Markets',
        link: [
          { rel: 'canonical', href: `${window.location.href}` },
        ],
        meta: [
          { name: 'description', content: this.currentHeader.seoDescription },
          { itemprop: 'name', content: this.currentHeader.seoTitle },
          { itemprop: 'description', content: this.currentHeader.seoDescription },
          { property: 'og:title', content: this.currentHeader.seoTitle },
          { property: 'og:type', content: 'website' },
          { property: 'og:description', content: this.currentHeader.seoDescription },
          { property: 'og:site_name', content: BRAND },
          { property: 'og:url', content: `${window.location.href}` },
        ],
      };
    }
    return {
      title: 'Markets',
    };
  },
  computed: {
    ...mapState('browse', [
      'selectedFilters',
      'selectedOrder',
      'selectedTimespan',
      'filteredMarkets',
      'headers',
      'latestHeaderId',
    ]),
    ...mapState('user', ['marketDisplay']),
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapGetters('user', ['isLoggedIn']),
    ...mapState('app', ['previousRoute']),
    items() {
      const markets = this.filteredMarkets.data === null ? [] : this.filteredMarkets.data;
      const statuses = [
        constants.MARKET_SETTLED,
        constants.MARKET_EXPIRED,
        constants.MARKET_SUSPENDED,
      ];
      return markets
        .filter(m => !statuses.includes(m.status));
    },
    showNoMarketsMessage() {
      return !this.showInitialSpinner && !this.isLoading && !this.isInfiniteLoading && this.items.length === 0;
    },
    currentHeader() {
      const header = this.headers ? this.headers.find(h => h.headerId === this.headerId) : null;
      return header || null;
    },
    currentHeaderName() {
      const header = this.currentHeader;
      if (header) {
        return header.name;
      }
      return this.selectedFilters.length ? 'Filtered Markets' : 'All Markets';
    },
    filtering() {
      return this.selectedFilters.toString() + this.selectedOrder.name + this.selectedTimespan.name;
    },
    showSpinner() {
      return !this.embedded || (this.embedded && this.initialLoadDone);
    },
    showInitialSpinner() {
      return !this.initialLoadDone || (this.embedded && !this.embeddedInitialFontsReady);
    },
    marketSkeletonCount() {
      return this.isLayoutDesktop ? 9 : 6;
    },
  },
  methods: {
    ...mapActions('browse', [
      'clearFilters',
      'clearTimespan',
      'clearOrder',
      'getFilters',
      'getFilteredMarkets',
      'setLatestHeaderId',
      'resetFilteredMarkets',
    ]),
    ...mapActions('user', ['updateMarketDisplay']),
    ...mapActions('notifications', ['setSideNotification']),
    fetchData() {
      this.isLoading = true;
      this.infiniteLoader = null;
      this.initialLoadDone = false;
      if (this.embedded) {
        this.embeddedInitialFontsReady = false;
      }
      if (this.isFiltering) return;
      if (Number.isInteger(this.headerId)) {
        this.notifyEmbeddedInitialLoadState(true);
        log.info('[MARKETS] Fetching Data');
        this.isFiltering = true;
        this.reorganizeTags();
        this.getFilters(this.headerId).then(() => {
          this.getMarkets(true).then(() => {
            this.isFiltering = false;
            this.isLoading = false;
            this.initialLoadDone = true;
            this.waitForEmbeddedInitialFonts()
              .then(() => {
                this.notifyEmbeddedInitialLoadState(false);
              });
          }).catch(() => {
            this.isLoading = false;
            this.isFiltering = false;
            this.initialLoadDone = true;
            this.waitForEmbeddedInitialFonts()
              .then(() => {
                this.notifyEmbeddedInitialLoadState(false);
              });
          });
        }).catch(() => {
          this.isFiltering = false;
          this.isLoading = false;
          this.initialLoadDone = true;
          this.waitForEmbeddedInitialFonts()
            .then(() => {
              this.notifyEmbeddedInitialLoadState(false);
            });
        });
      }
    },
    getMarkets(reset = false) {
      return this.getFilteredMarkets({
        headerId: this.headerId || 0,
        reset,
      });
    },
    onLayoutToggle(layout) {
      this.updateMarketDisplay(layout);
    },
    notifyEmbeddedInitialLoadState(isLoading) {
      if (this.embedded) {
        this.$emit('embeddedInitialLoadStateChange', isLoading);
      }
    },
    waitForEmbeddedInitialFonts() {
      if (!this.embedded || this.embeddedInitialFontsReady) {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      if (typeof document === 'undefined' || !document.fonts || typeof document.fonts.load !== 'function') {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      const requiredFontFaces = [
        '500 14px Roboto',
        '300 12px Roboto',
        '300 24px "Roboto Mono"',
      ];

      if (requiredFontFaces.every(fontFace => document.fonts.check(fontFace))) {
        this.embeddedInitialFontsReady = true;
        return Promise.resolve();
      }

      return Promise.race([
        Promise.all([
          ...requiredFontFaces.map(fontFace => document.fonts.load(fontFace)),
        ]),
        new Promise((resolve) => {
          setTimeout(resolve, HOMEPAGE_FONT_LOAD_FALLBACK_MS);
        }),
      ]).catch(() => {})
        .then(() => {
          this.embeddedInitialFontsReady = true;
        });
    },
    filteringChanged() {
      if (this.watchersDisabled) return;

      log.info(`[MARKETS] Filtering. Is in Progress: ${this.isFiltering.toString()}`);
      if (this.isFiltering) return;
      if (this.embedded) {
        this.resetFilteredMarkets();
        this.fetchData();
      } else {
        this.$router.push({
          name: 'market-list',
          params: {
            headerId: this.headerId,
            headerUrl: this.headerUrl,
          },
          query: {
            filterIds: this.selectedFilters.join(','),
            sort: this.selectedOrder.sort,
            sortParameter: this.selectedTimespan.parameter,
          },
        });
        this.fetchData();
      }
    },
    headerIdChanged(newValue, oldValue) {
      if (this.embedded) {
        this.resetFilteredMarkets();
        this.setLatestHeaderId(newValue);
        this.clearFilters();
        this.clearOrder();
        this.clearTimespan();
        this.fetchData();
      } else {
        this.resetFilteredMarkets();
        this.fetchData();
      }
    },
    goInfinite(infi) {
      this.infiniteLoader = infi;

      if (this.filteredMarkets.canLoadMore) {
        this.isInfiniteLoading = true;
        log.info(`[MARKETS] go infinite, initialLoadDone=${this.initialLoadDone}`);
        this.getMarkets(false);
      }
    },

  },
  watch: {
    headerId: {
      handler: 'headerIdChanged',
      immediate: true,
    },
    filtering: 'filteringChanged',
    isLoggedIn: 'fetchData',
    items() {
      if (this.infiniteLoader) {
        this.infiniteLoader.loaded();
        this.isInfiniteLoading = false;
        if (!this.filteredMarkets.canLoadMore) {
          this.infiniteLoader.complete();
        }
      }
    },
  },
  created() {
    if (this.notificationText && this.notificationLevel) {
      this.setSideNotification({
        text: this.notificationText,
        level: this.notificationLevel,
      });
    }
  },
  beforeDestroy() {
    if (this.embedded) {
      this.watchersDisabled = true;
      this.resetFilteredMarkets();
      this.clearFilters();
      this.clearOrder();
      this.clearTimespan();
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-markets/page-markets.js